import { useId, type ReactElement } from 'react';
import type { ConversationItem } from '@chatapp-inbox/shared';
import { Button } from './Button';
import { bitrixNoticeText, bitrixSyncErrorText } from './bitrix-text';
import { useConversationBitrix, useSyncBitrix } from './use-bitrix';
import shared from './shared.module.css';
import styles from './BitrixSection.module.css';

/** Só abre endereço `https://`: o link vem do servidor, mas nunca vira `javascript:` ou `data:`. */
const isSafeUrl = (url: string): boolean => url.startsWith('https://');

/**
 * Seção "Bitrix" do painel: o Lead que esta conversa tem no Bitrix (criado pelo CladvApp ou já existente),
 * com o link para abri-lo lá, ou o motivo de não haver um. Integração desligada: a seção não aparece.
 */
export function BitrixSection({
  conversation,
}: {
  conversation: ConversationItem;
}): ReactElement | null {
  const headingId = useId();
  const bitrix = useConversationBitrix(conversation.id);
  const sync = useSyncBitrix(conversation.id);
  const data = bitrix.data;
  if (!data || data.state === 'off') return null;

  const checking = data.state === 'pending' || data.rechecking || sync.isPending;
  const { lead } = data;

  return (
    <section aria-labelledby={headingId} className={shared.section}>
      <h3 id={headingId} className={shared.heading}>
        Bitrix
      </h3>

      {lead && (
        <div className={styles.lead}>
          <p className={styles.title} dir="auto">
            <span className={styles.number}>Lead {lead.id}</span>
            {lead.title ? <> · {lead.title}</> : null}
          </p>
          <p className={shared.hint}>
            {(lead.stageName ?? lead.stageId) ? `Etapa: ${lead.stageName ?? lead.stageId}. ` : ''}
            {lead.createdByApp ? 'Criado pelo CladvApp.' : 'Já existia no Bitrix.'}
          </p>
          {isSafeUrl(lead.url) && (
            <a className={styles.link} href={lead.url} target="_blank" rel="noopener noreferrer">
              Abrir no Bitrix
            </a>
          )}
        </div>
      )}

      {!lead && data.state === 'pending' && <p className={shared.hint}>Conferindo no Bitrix…</p>}

      {!lead && (data.state === 'none' || data.state === 'error') && (
        <p className={data.state === 'error' ? shared.alert : shared.hint}>
          {bitrixNoticeText(data.state, data.errorCode)}
        </p>
      )}

      {/* Região educada que sempre existe: o leitor de tela anuncia quando a conferência começa e termina. */}
      <p role="status" className={shared.hint}>
        {checking ? 'Conferindo no Bitrix…' : ''}
      </p>

      {sync.isError && (
        <p role="alert" className={shared.alert}>
          {bitrixSyncErrorText(sync.error)}
        </p>
      )}

      {conversation.canWrite && (
        <div className={styles.actions}>
          <Button busy={checking} onClick={() => sync.mutate()}>
            Verificar de novo
          </Button>
        </div>
      )}
    </section>
  );
}
